import React from 'react';
import PropTypes from 'prop-types';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import SVG from 'react-inlinesvg';
import SloganImg from '../../assets/images/slogan.svg';
import FooterComponent from './Footer.styles';
import { useTranslation } from 'react-i18next';
import phoneIcon from '../../assets/images/phone.svg';
import envelopeIcon from '../../assets/images/envelope.svg';
import {
  SubscribeWrapper,
  FsSocialWrapper,
  SocialWrapper,
  LinksWrapper,
  Row,
  Slogan,
  FsGrid,
  Grid,
  ColumnsWrapper,
  ContactInfo,
} from './Footer.styles';

import { useDeviceResize } from '../../hooks';

import SubscribeLoader from './SubscribeLoader.jsx';
import SocialLinks from '../shared/SocialLinks/SocialLinks.jsx';
import LinksList from './LinksList/LinksList.jsx';


function useIsCanada() {
  const [isCanada, setIsCanada] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const getCountryFromSdk = () => {
      const O  = window.OneTrust;
      const OT = window.Optanon;

      const domain  = O?.GetDomainData?.()  ?? OT?.GetDomainData?.();
      const consent = O?.GetConsentData?.() ?? OT?.GetConsentData?.();

      const fromDomainDeep  = domain?.ConsentIntegrationData?.consentPayload?.dsDataElements?.Country;
      const fromConsentDeep = consent?.ConsentIntegrationData?.consentPayload?.dsDataElements?.Country;
      const fromConsentRoot =
        consent?.dsDataElements?.Country || consent?.dsDataElements?.country || null;

      const fromDomainGeo =
        domain?.Geolocation?.countryCode ||
        domain?.geo?.countryCode ||
        domain?.geolocation?.countryCode ||
        domain?.location?.countryCode ||
        null;

      const country = (fromDomainDeep || fromConsentDeep || fromConsentRoot || fromDomainGeo) ?? null;
      return country ? String(country).toUpperCase() : null;
    };

    let attempts = 0;
    let timer = null;
    const maxAttempts = 12;
    const baseDelay   = 400;

    const resolveCountry = (label) => {
      const country = getCountryFromSdk();
      if (country) {
        setIsCanada(country === "CA");
        cleanup();
        return true;
      } else {
        return false;
      }
    };

    const retry = () => {
      if (attempts >= maxAttempts) return;
      const delay = Math.min(baseDelay * Math.pow(1.35, attempts), 2000);
      attempts += 1;
      timer = setTimeout(() => {
        if (!resolveCountry(`retry-${attempts}`)) retry();
      }, delay);
    };

    const onGroupsUpdated = () => {
      if (!resolveCountry("OneTrustGroupsUpdated")) retry();
    };
    const onWrapper = () => {
      if (!resolveCountry("OptanonWrapper")) retry();
    };

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("OneTrustGroupsUpdated", onGroupsUpdated);
      window.removeEventListener("OptanonWrapper", onWrapper);
    };

    window.addEventListener("OneTrustGroupsUpdated", onGroupsUpdated);
    window.addEventListener("OptanonWrapper", onWrapper);

    if (!resolveCountry("immediate")) retry();

    return cleanup;
  }, []);

  return isCanada;
}


/**
 * Opens the OneTrust preference centre. The link always renders, so this no-ops
 * when the SDK is absent (local/staging hosts aren't registered with OneTrust,
 * and blockers can prevent it loading in production).
 */
const openPrivacyChoices = (event) => {
  event.preventDefault();
  const sdk = window.OneTrust || window.Optanon;
  sdk?.ToggleInfoDisplay?.();
};

const PrivacyChoicesLink = ({ label }) => (
  <li>
    <a href="#" className="privacy-choices" onClick={openPrivacyChoices}>
      {label}
    </a>
  </li>
);

PrivacyChoicesLink.propTypes = {
  label: PropTypes.string.isRequired,
};

const CanadaContactInfo = () => (
  <ContactInfo aria-label="Contact Information">
    <div className="contact-row">
      <span className="contact-icon" aria-hidden="true">
        <img width="20" height="20" className="phone-icon" src={phoneIcon} alt="phone icon" />
      </span>
      <a href="tel:8005903797" className="text-white" aria-label="Call 800-590-3797">800-590-3797</a>
    </div>
    <div className="contact-row">
      <span className="contact-icon" aria-hidden="true">
        <img width="20" height="20" className="envelope-icon" src={envelopeIcon} alt="envelope icon" />
      </span>
      <address className="mb-0" style={{ fontStyle: 'normal' }}>
        1016 Weisgarber Rd, Knoxville, TN 37909
      </address>
    </div>
  </ContactInfo>
);

const Footer = (props) => {
  const {
    sitecoreContext = {},
    variant = 'red',
  } = props;
  const { globalContent = {}, site, context = {} } = sitecoreContext;
  const { footer = {}, homepageLink } = globalContent;
  const {
    companyTagLineSvg = {},
    linkColumns = [],
    socialLinks = [],
    utilityLinks = [],
    widgets = [],
  } = footer;
  const device = useDeviceResize();
  const isCanada = useIsCanada();
  const { t } = useTranslation();
  const privacyChoicesLabel = t('BushsBeans.Global.Footer.PrivacyChoices', 'Privacy Choices');

  const hideSubscribeCanada = site?.name === 'bushs-beans-canada';
  if (site?.name === 'bushs-beans-foodservices') {
    const endpoint = widgets.find((widget) => widget?.type === 'industry-subscribe-form')?.actionDestination;
    return (
      <FooterComponent variant={variant} className="hide-on-print">
        <FsGrid $hideSubscribe={hideSubscribeCanada}>
          {!hideSubscribeCanada && (
            <SubscribeWrapper>
              <span className="h1">
                sign up for industry updates
              </span>
              <SubscribeLoader
                endpoint={endpoint}
                placeholder={t(
                  'BushsBeans.Global.Footer.SubscribePlaceholder',
                  'enter your email'
                )}
                success={t(
                  'BushsBeans.Global.Footer.SubscribeSuccessMessage',
                  'thanks for subscribing'
                )}
                error={t(
                  'BushsBeans.Global.Footer.SubscribeErrorMessage',
                  'please enter valid email'
                )}
              />
              {isCanada && <CanadaContactInfo />}
            </SubscribeWrapper>
          )}

          <ColumnsWrapper className="fs-foter-columns" variant="blue">
            {linkColumns.map((links, index) => <LinksList key={`LinksList-${index}`} {...links} />)}
          </ColumnsWrapper>

          {socialLinks && (
            <FsSocialWrapper variant={variant}>
              <h3 className="list-title emphasis small">
                {t('BushsBeans.Global.Footer.SocialTitle', 'Connect')}
              </h3>
              <SocialLinks links={socialLinks} />
            </FsSocialWrapper>
          )}
        </FsGrid>

        <Row>
          <Slogan>
            <SVG src={SloganImg} />
          </Slogan>
        </Row>
        <Row variant={variant}>
          {utilityLinks && (
            <>
              <nav>
                <ul>
                  {utilityLinks.map(({ href, path, target, title }, index) => (
                      <li key={`utilityLink-${index}_${href || path}`}>
                        <a href={href || path} target={target || '_self'}>
                          {title}
                        </a>
                      </li>
                    ))}
                  <PrivacyChoicesLink label={privacyChoicesLabel} />
                </ul>
              </nav>
              <small>
                {t(
                  'BushsBeans.Global.Footer.copywrite',
                  '© BUSH BROTHERS & COMPANY. ALL RIGHTS RESERVED.'
                )}
              </small>
            </>
          )}
        </Row>
      </FooterComponent>
    );
  } else {
    const endpoint = widgets.find((widget) => widget?.type === 'subscribe-form')?.actionDestination;
    return (
      <FooterComponent variant={variant} className={`hide-on-print ${site?.name}`}>
        <Grid $hideSubscribe={hideSubscribeCanada}>
          {!hideSubscribeCanada && (
          <SubscribeWrapper>
            <span className="h1">{t('BushsBeans.Global.Footer.SubscribeTitle', 'subscribe for bean news')}</span>
            <SubscribeLoader
              endpoint={endpoint}
              placeholder={t('BushsBeans.Global.Footer.SubscribePlaceholder', 'enter your email')}
              success={t('BushsBeans.Global.Footer.SubscribeSuccessMessage', 'Thanks for signing up!')}
              error={t('BushsBeans.Global.Footer.SubscribeErrorMessage', 'please enter valid email')}
            />
            {isCanada && <CanadaContactInfo />}
          </SubscribeWrapper>
          )}
          {socialLinks && (
            <SocialWrapper variant={variant}>
              <h3 className="list-title emphasis small">{t('BushsBeans.Global.Footer.SocialTitle', 'Connect')}</h3>
              <SocialLinks links={socialLinks} />
            </SocialWrapper>
          )}
          <LinksWrapper className={(device === 'desktop') ? 'link-wrapper' : 'hiding'}
                        variant={variant}>{linkColumns.map((links, index) => <LinksList
            key={`LinksList-${index}`} {...links} />)}</LinksWrapper>
        </Grid>

        <Row>
          <Slogan>
            <SVG src={companyTagLineSvg?.src} />
          </Slogan>
        </Row>
        <Row variant={variant} className="footer-utility-links">
          {utilityLinks && (
            <>
              <nav>
                <ul>
                  {utilityLinks.map(({ href, path, target, title }, index) => (
                    <li key={`utilityLink-${index}_${href || path}`}>
                      <a href={href || path} target={target}>{title}</a>
                    </li>
                  ))}
                  <PrivacyChoicesLink label={privacyChoicesLabel} />
                </ul>
              </nav>
              <small>{t(
                'BushsBeans.Global.Footer.copywrite',
                '© BUSH BROTHERS & COMPANY. ALL RIGHTS RESERVED.'
              )}</small>
            </>
          )}
        </Row>
        <div id="footer-notifications" aria-live="polite" />
      </FooterComponent>
    );
  }
};

Footer.propTypes = {
  sitecoreContext: PropTypes.shape({
    globalContent: PropTypes.shape({
      footer: PropTypes.shape({
        companyTagLineSvg: PropTypes.object,
        utilityLinks: PropTypes.shape({
          title: PropTypes.string,
          links: PropTypes.array,
        }),
        linkColumns: PropTypes.shape({
          columnTitle: PropTypes.string,
          links: PropTypes.array,
        }),
        socialLinks: PropTypes.shape({
          title: PropTypes.string,
          links: PropTypes.array,
        }),
        widgets: PropTypes.array,
      }),
    }),
  }),
};

export default withSitecoreContext()(Footer);